import { getValueCookie } from '../../services/cookieService'
import { getPurePriceMargin } from '../../functions/generalFunctions'

/**
 * Builds one Sales Order cart line from a /v3/salesorder/catalog (or
 * item-by-code) row — shared by CatalogueV3.js and SearchUPCV3.js so both
 * produce the exact same shape SalesOrderV3.js expects.
 *
 * Price/cost come ONLY from product_pricing (already joined into `row` by the
 * backend) — never calculated here. If standardPrice is null, the line still
 * gets added (so the salesperson isn't blocked from building the rest of the
 * order) but flagged via `_noPricingConfigured`, which SalesOrderV3.js's
 * render shows as a visible warning instead of a silent/wrong price.
 */
export function buildCartLine(row, quantity, customerName) {
    const unitPrice = (row.standardPrice !== null && row.standardPrice !== undefined) ? Number(row.standardPrice) : 0
    const cost = (row.cost !== null && row.cost !== undefined) ? Number(row.cost) : null
    const noPricingConfigured = row.standardPrice === null || row.standardPrice === undefined

    // Long description (2026-08-20, client's ask) — sourced from CI_Item
    // (mas90_mirror__CI_Item / alt__mas90_mirror__CI_Item, joined into `row`
    // as `longDescription` by salesOrderCatalog.ts), NOT itemHeader.completeDesc
    // — the short `description` is the last-resort fallback for an item CI_Item
    // hasn't synced yet.
    const longDesc = row.longDescription || row.completeDesc || row.description

    const line = {
        itemCode: row.itemCode,
        abbreviatedDesc: longDesc,
        completeDesc: longDesc,
        itemURLImage: row.itemURLImage,
        upc: row.upc,
        upc2: row.upc2,
        caseupc: row.caseupc,
        quantity: row.quantity || 0, // on-hand, display only
        ShipWeight: Number(row.shipWeight) || 0,
        quantityOrdered: quantity,
        UnitPrice: unitPrice,
        totalCost: unitPrice * quantity,
        unitCost: cost,
        LastTotalUnitCost: cost, // legacy field name — calculateTotals() in SalesOrderV3.js still reads this
        minPrice: (row.minPrice !== null && row.minPrice !== undefined) ? Number(row.minPrice) : null,
        maxPrice: (row.maxPrice !== null && row.maxPrice !== undefined) ? Number(row.maxPrice) : null,
        // Hard price floor (2026-08-19) — the Level 1/standard price for this
        // item, independent of whichever tier the customer is actually being
        // charged. checkPriceBounds() in SalesOrderV3.js enforces this as the
        // absolute minimum a manually-typed Unit Price can go to.
        level1Price: (row.level1Price !== null && row.level1Price !== undefined) ? Number(row.level1Price) : null,
        // Level 3 below-minimum exception (2026-08-20, migration 017) — when
        // set for this item+company, checkPriceBounds() in SalesOrderV3.js
        // uses cost instead of minPrice as the manual-price-edit floor.
        allowLevel3BelowCost: !!row.allowLevel3BelowCost,
        costAtSale: cost,
        standardPriceAtSale: unitPrice,
        finalPrice: unitPrice,
        Visible: quantity > 0 ? 1 : 0,
        LineSequence: 0,
        quantityShipped: 0,
        Comment: '',
        PureMargin: undefined,
        HaveUPC: !!(row.upc || row.upc2 || row.caseupc),
        // Already correctly priced — SalesOrderV3.js's applyStandardPricing()
        // skips lines that already carry this marker.
        _pricingApplied: true,
        _noPricingConfigured: noPricingConfigured,
    }

    // Altitude box-tracking — same fields V2 set on add (ItemCard.js
    // UpdatePrincipalItemList), needed by AsignLineSequence()'s confirmMargin check.
    if (getValueCookie('CompanyId') === '6') {
        line.Comment = 'BOX:' + (customerName || '')
        line.PureMargin = getPurePriceMargin(unitPrice, cost)
    }

    return line
}
